import {
  CardTransactionError,
  CardTransactionStatusV2,
  CardTransactionStatusV2Type,
  Factory,
  PaymentServiceProvider,
  ReminderChannel,
} from '../..';
import { SentStatus } from '../sent-amesto';
import { DebtCollectionControlStatusType, DebtCollectionSnapshot } from './debt-collection';
import { PaymentSplit } from './payment-split';

export enum PaymentEventType {
  CardTransactionSucceeded = 'CardTransactionSucceeded',
  CardTransactionFailed = 'CardTransactionFailed',
  CardRefundIssued = 'CardRefundIssued',
  InvoiceIssued = 'InvoiceIssued',
  AvtaleGiroIssued = 'AvtaleGiroIssued',
  CreditNoteIssued = 'CreditNoteIssued',
  ReminderSent = 'ReminderSent',
  CustomerLoss = 'CustomerLoss',
  BalanceDeducted = 'BalanceDeducted',
  DebtCollectionIssued = 'DebtCollectionIssued',
}
export class PaymentEvent {
  type: PaymentEventType;

  created: Date;
  constructor(json: any) {
    this.type = json.type;

    this.created = new Date(json.created);
  }

  getAmount(event: PaymentEvent): number | undefined {
    return PaymentEvent.getAmount(event);
  }

  getPaidDate(event: PaymentEvent): Date | undefined {
    return PaymentEvent.getPaidDate(event);
  }

  getCaptureDate(event: PaymentEvent): Date | undefined {
    return PaymentEvent.getCaptureDate(event);
  }

  isPartlyPaid(event: PaymentEvent): boolean {
    return PaymentEvent.isPartlyPaid(event);
  }

  public static getFactory(): Factory<PaymentEvent> {
    return new (class implements Factory<PaymentEvent> {
      make(json: any): PaymentEvent {
        switch (json.type) {
          case PaymentEventType.CardTransactionSucceeded:
            return new CardTransactionSucceeded(json);
          case PaymentEventType.CardTransactionFailed:
            return new CardTransactionFailed(json);
          case PaymentEventType.CardRefundIssued:
            return new CardRefundIssued(json);
          case PaymentEventType.InvoiceIssued:
            return new InvoiceIssued(json);
          case PaymentEventType.AvtaleGiroIssued:
            return new AvtaleGiroIssued(json);
          case PaymentEventType.CreditNoteIssued:
            return new CreditNoteIssued(json);
          case PaymentEventType.ReminderSent:
            return new ReminderSent(json);
          case PaymentEventType.CustomerLoss:
            return new CustomerLoss(json);
          case PaymentEventType.BalanceDeducted:
            return new BalanceDeducted(json);
          case PaymentEventType.DebtCollectionIssued:
            return new DebtCollectionIssued(json);
          default:
            throw new Error('Unrecognized PaymentEvents type (' + json.type + ').');
        }
      }
      getTableName(): string {
        throw new Error('getTableName() should never be called on a PaymentEvents.');
      }
    })();
  }

  public static isCredit(event: PaymentEvent): CreditNoteIssued | boolean {
    const e = PaymentEvent.getFactory().make(event);

    if (e instanceof CreditNoteIssued) {
      return e;
    }
    return false;
  }

  public static getAliasId(event: PaymentEvent): number | undefined {
    const e = PaymentEvent.getFactory().make(event);

    if (
      e instanceof CardTransactionSucceeded ||
      e instanceof InvoiceIssued ||
      e instanceof AvtaleGiroIssued ||
      e instanceof CreditNoteIssued ||
      e instanceof CardRefundIssued
    ) {
      return e.aliasId;
    } else {
      return undefined;
    }
  }

  public static getReferenceAliasId(event: PaymentEvent): number | undefined {
    const e = PaymentEvent.getFactory().make(event);
    if (e instanceof CustomerLoss || e instanceof DebtCollectionIssued) {
      return e.paymentReferenceAliasId;
    } else {
      return undefined;
    }
  }

  public static getAmount(event: PaymentEvent): number | undefined {
    const e = PaymentEvent.getFactory().make(event);

    if (
      e instanceof CardTransactionSucceeded ||
      e instanceof CardTransactionFailed ||
      e instanceof InvoiceIssued ||
      e instanceof AvtaleGiroIssued ||
      e instanceof CreditNoteIssued ||
      e instanceof CardRefundIssued ||
      e instanceof CustomerLoss
    ) {
      return e.amount;
    } else if (e instanceof BalanceDeducted) {
      return e.deductedAmount;
    } else {
      return undefined;
    }
  }

  public static getInvoiceOrAvtalegiro(event: PaymentEvent): InvoiceIssued | AvtaleGiroIssued | undefined {
    const e = PaymentEvent.getFactory().make(event);

    if (e instanceof InvoiceIssued || e instanceof AvtaleGiroIssued) {
      return e;
    } else {
      return undefined;
    }
  }

  public static getInvoiceFee(event: PaymentEvent): number | undefined {
    const e = PaymentEvent.getFactory().make(event);

    if (e instanceof InvoiceIssued) {
      return e.invoiceFee;
    } else {
      return undefined;
    }
  }

  public static getCardFailedReason(event: PaymentEvent): string | undefined {
    const e = PaymentEvent.getFactory().make(event);

    if (e instanceof CardTransactionFailed) {
      return CardTransactionError.getFactory().make(e.reason).text;
    } else {
      return undefined;
    }
  }

  public static getPaidDate(event: PaymentEvent) {
    const e = PaymentEvent.getFactory().make(event);

    if (e instanceof InvoiceIssued || e instanceof AvtaleGiroIssued) {
      return e.paidDate;
    } else if (e instanceof CardTransactionSucceeded) {
      return e.getCaptureDate(e);
    } else {
      return undefined;
    }
  }

  public static getCaptureDate(event: PaymentEvent): Date | undefined {
    const e = PaymentEvent.getFactory().make(event);

    if (e instanceof CardTransactionSucceeded) {
      if (e.transactionStatus.type === CardTransactionStatusV2Type.Captured) return e.transactionStatus.created;
    } else {
      return undefined;
    }
    return undefined;
  }

  public static isPartlyPaid(event: PaymentEvent): boolean {
    const e = PaymentEvent.getFactory().make(event);

    if (e instanceof InvoiceIssued) {
      return e.partlyPaid !== undefined;
    } else {
      return false;
    }
  }
}

export class CardTransactionSucceeded extends PaymentEvent {
  aliasId: number;
  voucherNumber: number;
  transactionStatus: CardTransactionStatusV2;
  amount: number;
  reference: string;
  provider: PaymentServiceProvider;
  paymentMethodId: string;

  constructor(json: any) {
    super(json);
    this.aliasId = json.aliasId;
    this.voucherNumber = json.voucherNumber;
    this.transactionStatus = new CardTransactionStatusV2(json.transactionStatus);
    this.amount = Number(json.amount);
    this.reference = json.reference;
    this.provider = json.provider;
    this.paymentMethodId = json.paymentMethodId;
  }
}

export class CardTransactionFailed extends PaymentEvent {
  amount: number;
  reason: CardTransactionError;
  paymentMethodId: string;
  provider: PaymentServiceProvider;

  constructor(json: any) {
    super(json);
    this.amount = Number(json.amount);
    this.reason = new CardTransactionError(json.reason);
    this.paymentMethodId = json.paymentMethodId;
    this.provider = json.provider;
  }
}

export class InvoiceIssued extends PaymentEvent {
  aliasId: number;
  voucherNumber: number;
  amount: number;
  batch?: number;
  partlyPaid?: number;
  partialPayments: PartialPayment[];
  invoiceFee: number;
  terminationFee?: number;
  terminationUnpaidCarryovers?: TerminationUnpaidCarryoverLineItem[];
  lateFee?: number;
  dueDate: Date;
  paidDate?: Date;
  sentAmesto?: SentStatus;
  sentOrbyt?: SentStatus;
  sentPostnord?: SentStatus;
  sentEmail?: SentStatus;
  splits: PaymentSplit[];

  constructor(json: any) {
    super(json);
    this.aliasId = json.aliasId;
    this.voucherNumber = json.voucherNumber;
    this.amount = Number(json.amount);
    this.batch = json.batch;
    this.partlyPaid = json.partlyPaid !== undefined ? Number(json.partlyPaid) : undefined;
    this.partialPayments = json.partialPayments
      ? json.partialPayments.map((partialPayment: any) => new PartialPayment(partialPayment))
      : [];
    this.invoiceFee = Number(json.invoiceFee);
    this.dueDate = new Date(json.dueDate);
    this.lateFee = json.lateFee !== undefined ? Number(json.lateFee) : undefined;
    this.terminationFee = json.terminationFee !== undefined ? Number(json.terminationFee) : undefined;
    this.terminationUnpaidCarryovers = json.terminationUnpaidCarryovers
      ? json.terminationUnpaidCarryovers.map((item: any) => new TerminationUnpaidCarryoverLineItem(item))
      : undefined;
    this.paidDate = json.paidDate ? new Date(json.paidDate) : undefined;
    this.sentAmesto = json.sentAmesto ? SentStatus.getFactory().make(json.sentAmesto) : undefined;
    this.sentOrbyt = json.sentOrbyt ? SentStatus.getFactory().make(json.sentOrbyt) : undefined;
    this.sentPostnord = json.sentPostnord ? SentStatus.getFactory().make(json.sentPostnord) : undefined;
    this.sentEmail = json.sentEmail ? SentStatus.getFactory().make(json.sentEmail) : undefined;
    this.splits = json.splits ? json.splits.map((split: any) => new PaymentSplit(split)) : [];
  }
}

export class AvtaleGiroIssued extends PaymentEvent {
  aliasId: number;
  voucherNumber: number;
  amount: number;
  batch?: number;
  dueDate: Date;
  paidDate?: Date;
  sentAmesto?: SentStatus;
  splits: PaymentSplit[];

  constructor(json: any) {
    super(json);
    this.aliasId = json.aliasId;
    this.voucherNumber = json.voucherNumber;
    this.amount = Number(json.amount);
    this.batch = json.batch;
    this.dueDate = new Date(json.dueDate);
    this.paidDate = json.paidDate ? new Date(json.paidDate) : undefined;
    this.sentAmesto = json.sentAmesto ? SentStatus.getFactory().make(json.sentAmesto) : undefined;
    this.splits = json.splits ? json.splits.map((split: any) => new PaymentSplit(split)) : [];
  }
}

export class CreditNoteIssued extends PaymentEvent {
  aliasId: number;
  voucherNumber: number;
  amount: number;
  note?: string;
  batch?: number;

  constructor(json: any) {
    super(json);
    this.aliasId = json.aliasId;
    this.voucherNumber = json.voucherNumber;
    this.amount = Number(json.amount);
    this.note = json.note ?? undefined;
    this.batch = json.batch;
  }
}

export class CardRefundIssued extends PaymentEvent {
  aliasId: number;
  voucherNumber: number;
  batch?: number;
  amount: number;
  splits: PaymentSplit[];
  sentAmesto?: SentStatus;
  sentEmail?: SentStatus;
  reference: string;

  constructor(json: any) {
    super(json);
    this.aliasId = json.aliasId;
    this.voucherNumber = json.voucherNumber;
    this.batch = json.batch;
    this.amount = Number(json.amount);
    this.splits = json.splits ? json.splits.map((split: any) => new PaymentSplit(split)) : [];
    this.sentAmesto = json.sentAmesto ? SentStatus.getFactory().make(json.sentAmesto) : undefined;
    this.sentEmail = json.sentEmail ? SentStatus.getFactory().make(json.sentEmail) : undefined;
    this.reference = json.reference;
  }
}

export class DebtCollectionIssued extends PaymentEvent {
  paymentReferenceAliasId: number;
  controlStatus?: DebtCollectionControlStatusType;
  externalReference?: string;
  syncedAt: Date;
  history: DebtCollectionSnapshot[];

  constructor(json: any) {
    super(json);
    this.paymentReferenceAliasId = json.paymentReferenceAliasId;
    this.controlStatus = json.controlStatus ?? undefined;
    this.externalReference = json.externalReference ?? undefined;
    this.syncedAt = new Date(json.syncedAt);
    this.history = json.history ? json.history.map((snapshot: any) => new DebtCollectionSnapshot(snapshot)) : [];
  }

  getCurrentSnapshot(): DebtCollectionSnapshot | undefined {
    if (this.controlStatus === undefined) {
      return undefined;
    }

    return new DebtCollectionSnapshot({
      controlStatus: this.controlStatus,
      externalReference: this.externalReference,
      syncedAt: this.syncedAt,
    });
  }
}

export class CustomerLoss extends PaymentEvent {
  paymentReferenceAliasId: number;
  voucherNumber: number;
  batch?: number;
  sentAmesto?: SentStatus;
  amount: number;
  splits: PaymentSplit[];
  createdBy: string;

  constructor(json: any) {
    super(json);
    this.paymentReferenceAliasId = json.paymentReferenceAliasId;
    this.voucherNumber = json.voucherNumber;
    this.batch = json.batch !== undefined ? Number(json.batch) : undefined;
    this.sentAmesto = json.sentAmesto ? SentStatus.getFactory().make(json.sentAmesto) : undefined;
    this.amount = Number(json.amount);
    this.splits = json.splits ? json.splits.map((split: any) => new PaymentSplit(split)) : [];
    this.createdBy = json.createdBy;
  }
}

export class BalanceDeducted extends PaymentEvent {
  deductedAmount: number;
  reason: string;

  constructor(json: any) {
    super(json);
    this.deductedAmount = Number(json.deductedAmount);
    this.reason = json.reason;
  }
}

export class ReminderSent extends PaymentEvent {
  channel: ReminderChannel;

  constructor(json: any) {
    super(json);
    this.channel = new ReminderChannel(json.channel);
  }
}

export class TerminationUnpaidCarryoverLineItem {
  label: string;
  amount: number;
  periodFrom: Date;
  periodTo: Date;
  sourcePaymentId: string;
  sourceSpecificationId: string;

  constructor(json: any) {
    this.label = json.label;
    this.amount = Number(json.amount);
    this.periodFrom = new Date(json.periodFrom);
    this.periodTo = new Date(json.periodTo);
    this.sourcePaymentId = json.sourcePaymentId;
    this.sourceSpecificationId = json.sourceSpecificationId;
  }
}

export class PartialPayment {
  amount: number;
  date?: Date;
  constructor(json: any) {
    this.amount = Number(json.amount);
    this.date = json.date ? new Date(json.date) : undefined;
  }
}
